import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Lock, Mail, ArrowRight, Shield, ChevronLeft, Globe, Check, ChevronDown, Smartphone, RefreshCw, AlertCircle, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FLAG_EMOJIS = {
  US: "🇺🇸", IN: "🇮🇳", ES: "🇪🇸", FR: "🇫🇷", DE: "🇩🇪",
  JP: "🇯🇵", CN: "🇨🇳", SA: "🇸🇦", BR: "🇧🇷", KR: "🇰🇷",
};

const LOGO_URL = "https://assets-h2.izmocars.com/dealerlogos/qcoresecure.com.png";

export default function LoginPage() {
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [mfaMethod, setMfaMethod] = useState("totp");
  const [preAuthToken, setPreAuthToken] = useState("");
  const [availableMethods, setAvailableMethods] = useState([]);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState(null);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  const setErr = (message, code = null) => {
    setError(message);
    setErrorCode(code);
    // Parse lockout time from message (e.g. "Try again in 5 minute(s)." or "Try again in 45 second(s).")
    if (code === "ACCOUNT_LOCKED" || code === "RATE_LIMITED") {
      const minMatch = message.match(/(\d+)\s*minute/i);
      const secMatch = message.match(/(\d+)\s*second/i);
      if (minMatch) setLockoutSeconds(parseInt(minMatch[1]) * 60);
      else if (secMatch) setLockoutSeconds(parseInt(secMatch[1]));
    }
  };
  const clearErr = () => { setError(""); setErrorCode(null); setLockoutSeconds(0); };

  // Countdown timer
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const timer = setInterval(() => {
      setLockoutSeconds(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutSeconds > 0]);

  const { currentLanguage, setCurrentLanguage, t, supportedLanguages, detectBrowserLanguage } = useLanguage();
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    const savedLang = localStorage.getItem("qcore_language");
    if (!savedLang) {
      const detected = detectBrowserLanguage();
      if (detected !== currentLanguage) setCurrentLanguage(detected);
    }
  }, []);

  const selectedLang = supportedLanguages.find(l => l.code === currentLanguage);

  const handleCredentialsSubmit = async (e) => {
    e?.preventDefault();
    clearErr();
    // Manual validation — translated messages instead of browser native "Please fill out this field"
    if (!email) { setErr(t("validation.email_required", "Email is required")); return; }
    if (!email.includes("@")) { setErr(t("validation.email_invalid", "Please enter a valid email address")); return; }
    if (!password) { setErr(t("validation.password_required", "Password is required")); return; }
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.requires_2fa) {
        setPreAuthToken(data.pre_auth_token);
        setAvailableMethods(data.available_methods);
        setMfaMethod(data.available_methods[0] || "totp");
        setStep("mfa_choice");
      } else {
        const adminRoles = ["super_admin", "admin"];
        const dest = (from === "/dashboard" && adminRoles.includes(data.user?.role)) ? "/admin" : from;
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setErr(err.message || "Login failed. Please try again.", err.code || null);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaMethodSelect = async () => {
    clearErr();
    if (mfaMethod === "email_otp") {
      setLoading(true);
      try {
        await authApi.sendEmailOtp(preAuthToken);
        setEmailCodeSent(true);
        setStep("mfa_email");
        toast.success("OTP sent to your email");
      } catch (err) {
        setErr(err.message, err.code || null);
      } finally {
        setLoading(false);
      }
    } else {
      setStep("mfa_totp");
    }
  };

  const handleVerify2FA = async (method) => {
    if (otp.length < 6) return;
    clearErr();
    setLoading(true);
    try {
      const data = await authApi.verify2FA(preAuthToken, method, otp);
      completeLogin(data);
      const adminRoles = ["super_admin", "admin"];
      const dest = (from === "/dashboard" && adminRoles.includes(data.user?.role)) ? "/admin" : from;
      navigate(dest, { replace: true });
    } catch (err) {
      setErr(err.message || "Invalid verification code. Please try again.", err.code || null);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmailCode = async () => {
    setEmailCodeSent(false);
    try {
      await authApi.sendEmailOtp(preAuthToken);
      setEmailCodeSent(true);
      toast.success("New OTP sent");
    } catch (err) {
      setErr(err.message, err.code || null);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#020617] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 mb-20" data-testid="login-logo">
            <img src={LOGO_URL} alt="QCoreSecure" className="h-14 w-auto" />
          </Link>
          <h2 className="text-4xl font-bold text-white tracking-tight leading-tight mb-4">
            Secure Access to<br />QCoreSecure QRNG
          </h2>
          <p className="text-slate-400 max-w-md leading-relaxed">
            Access your QRNG dashboard, manage API keys, monitor usage, and integrate quantum entropy into your applications.
          </p>
        </div>
        <div className="relative z-10 flex gap-6">
          {[{ icon: Shield, label: "mTLS Secured" }, { icon: Lock, label: "FIPS 140-2" }].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
              <item.icon className="w-4 h-4 text-cyan-500/50" />
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col bg-[#F8FAFC]">
        {/* Language Selector */}
        <div className="flex justify-end p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 px-3 gap-2 text-slate-700 border-slate-200 bg-white hover:bg-slate-50" data-testid="login-language-selector">
                <Globe className="w-4 h-4 text-slate-500" />
                <span className="text-base">{FLAG_EMOJIS[selectedLang?.flag]}</span>
                <span className="text-sm">{selectedLang?.name}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {supportedLanguages.map((lang) => (
                <DropdownMenuItem key={lang.code} onClick={() => setCurrentLanguage(lang.code)}
                  className="flex items-center justify-between py-2" data-testid={`login-lang-option-${lang.code}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{FLAG_EMOJIS[lang.flag]}</span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-slate-900">{lang.name}</span>
                      <span className="text-xs text-slate-400">{lang.nativeName}</span>
                    </div>
                  </div>
                  {currentLanguage === lang.code && <Check className="w-4 h-4 text-cyan-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-12">
              <Link to="/" data-testid="login-logo-mobile">
                <img src={LOGO_URL} alt="QCoreSecure" className="h-12 w-auto" />
              </Link>
            </div>

            {/* Error banner — code-aware styling */}
            {error && (() => {
              const isLocked = errorCode === "ACCOUNT_LOCKED";
              const isRateLimited = errorCode === "RATE_LIMITED";
              return (
                <div
                  className={`flex items-start gap-3 p-3 mb-4 rounded border text-sm ${
                    isLocked
                      ? "bg-orange-50 border-orange-200 text-orange-800"
                      : isRateLimited
                      ? "bg-amber-50 border-amber-200 text-amber-800"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                  data-testid="login-error"
                >
                  <Lock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isLocked || isRateLimited ? "" : "hidden"}`} />
                  <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isLocked || isRateLimited ? "hidden" : ""}`} />
                  <div className="flex-1">
                    {isLocked && <p className="font-semibold mb-0.5">Account Temporarily Locked</p>}
                    {isRateLimited && <p className="font-semibold mb-0.5">Too Many Attempts</p>}
                    <p>{error}</p>
                    {(isLocked || isRateLimited) && lockoutSeconds > 0 && (
                      <div className="mt-2 flex items-center gap-2" data-testid="lockout-countdown">
                        <div className="flex items-center gap-1.5 bg-white/60 border border-current/20 rounded px-2 py-0.5 font-mono text-xs font-bold tabular-nums">
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          {Math.floor(lockoutSeconds / 60).toString().padStart(2, "0")}
                          :
                          {(lockoutSeconds % 60).toString().padStart(2, "0")}
                        </div>
                        <span className="text-xs opacity-70">until unlock</span>
                      </div>
                    )}
                    {(isLocked || isRateLimited) && lockoutSeconds === 0 && error && (
                      <p className="text-xs mt-1 opacity-70">You may now try again.</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ── Credentials ── */}
            {step === "credentials" && (
              <form onSubmit={handleCredentialsSubmit} noValidate data-testid="login-credentials-form">
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">{t("login.title")}</h1>
                <p className="text-sm text-slate-500 mb-8">
                  {t("login.no_account")}{" "}
                  <Link to="/register" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="login-register-link">{t("login.sign_up")}</Link>
                </p>
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t("label.email")}</Label>
                    <div className="relative mt-1.5">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder={t("login.email_placeholder")}
                        className="pl-10 h-11 bg-white border-slate-200" data-testid="login-email-input" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t("label.password")}</Label>
                      <Link to="/forgot-password" className="text-xs text-cyan-600 hover:text-cyan-700" data-testid="login-forgot-password">{t("login.forgot_password")}</Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                        placeholder={t("login.password_placeholder")}
                        className="pl-10 h-11 bg-white border-slate-200" data-testid="login-password-input" />
                    </div>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 transition-colors uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="login-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("action.next")} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <p className="text-xs text-slate-400 text-center">Protected by enterprise-grade security with mandatory MFA</p>
                </div>
              </form>
            )}

            {/* ── MFA Choice ── */}
            {step === "mfa_choice" && (
              <div data-testid="login-mfa-choice">
                <button onClick={() => setStep("credentials")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-8" data-testid="mfa-choice-back-btn">
                  <ChevronLeft className="w-4 h-4" /> {t("action.back")}
                </button>
                <div className="w-12 h-12 bg-cyan-50 border border-cyan-200 flex items-center justify-center mb-6">
                  <Shield className="w-6 h-6 text-cyan-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">{t("mfa.choose_method", "Choose Verification Method")}</h1>
                <p className="text-sm text-slate-500 mb-8">{t("mfa.choose_method_desc", "Select how you'd like to verify your identity")}</p>
                <RadioGroup value={mfaMethod} onValueChange={setMfaMethod} className="space-y-3 mb-8">
                  {availableMethods.includes("totp") && (
                    <label htmlFor="mfa-totp" className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all ${mfaMethod === "totp" ? "border-cyan-500 bg-cyan-50/50" : "border-slate-200"}`} data-testid="mfa-option-totp">
                      <RadioGroupItem value="totp" id="mfa-totp" />
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-purple-600" />
                      </div>
                      <div><div className="font-medium text-slate-900">{t("mfa.authenticator_app", "Authenticator App")}</div>
                        <div className="text-xs text-slate-500">{t("mfa.authenticator_desc", "Use Google Authenticator, Authy, or similar")}</div></div>
                    </label>
                  )}
                  {availableMethods.includes("email_otp") && (
                    <label htmlFor="mfa-email" className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all ${mfaMethod === "email_otp" ? "border-cyan-500 bg-cyan-50/50" : "border-slate-200"}`} data-testid="mfa-option-email">
                      <RadioGroupItem value="email_otp" id="mfa-email" />
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div><div className="font-medium text-slate-900">{t("mfa.email_code", "Email Code")}</div>
                        <div className="text-xs text-slate-500">{t("mfa.email_code_desc", "Receive a one-time code via email")}</div></div>
                    </label>
                  )}
                </RadioGroup>
                <button onClick={handleMfaMethodSelect} disabled={loading}
                  className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 transition-colors uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="mfa-continue-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("action.next")} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            )}

            {/* ── TOTP ── */}
            {step === "mfa_totp" && (
              <div data-testid="login-mfa-totp">
                <button onClick={() => setStep("mfa_choice")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-8" data-testid="mfa-totp-back-btn">
                  <ChevronLeft className="w-4 h-4" /> {t("action.back")}
                </button>
                <div className="w-12 h-12 bg-purple-50 border border-purple-200 flex items-center justify-center mb-6">
                  <Smartphone className="w-6 h-6 text-purple-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">{t("mfa.enter_totp", "Authenticator Code")}</h1>
                <p className="text-sm text-slate-500 mb-8">{t("mfa.enter_totp_desc", "Enter the 6-digit code from your authenticator app")}</p>
                <div className="mb-8">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="mfa-totp-input">
                    <InputOTPGroup className="gap-2 justify-center w-full">
                      {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} className="w-12 h-14 text-lg font-mono bg-white border-slate-200 text-slate-900" />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button onClick={() => handleVerify2FA("totp")} disabled={loading || otp.length < 6}
                  className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 transition-colors uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="mfa-totp-verify-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("action.login")}
                </button>
                <div className="mt-6 text-center">
                  <button onClick={() => setStep("mfa_choice")} className="text-xs text-cyan-600 hover:text-cyan-700" data-testid="mfa-recovery-link">
                    {t("mfa.use_recovery", "Use recovery code instead")}
                  </button>
                </div>
              </div>
            )}

            {/* ── Email OTP ── */}
            {step === "mfa_email" && (
              <div data-testid="login-mfa-email">
                <button onClick={() => setStep("mfa_choice")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-8" data-testid="mfa-email-back-btn">
                  <ChevronLeft className="w-4 h-4" /> {t("action.back")}
                </button>
                <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">{t("mfa.enter_email_code", "Email Verification")}</h1>
                <p className="text-sm text-slate-500 mb-8">{t("mfa.enter_email_code_desc", "Enter the 6-digit code sent to your email")}</p>
                {!emailCodeSent ? (
                  <div className="flex items-center justify-center py-4 mb-4">
                    <RefreshCw className="w-5 h-5 text-cyan-600 animate-spin mr-2" />
                    <span className="text-sm text-slate-600">{t("mfa.sending_code", "Sending code...")}</span>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="mfa-email-input">
                        <InputOTPGroup className="gap-2 justify-center w-full">
                          {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} className="w-12 h-14 text-lg font-mono bg-white border-slate-200 text-slate-900" />)}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    <button onClick={() => handleVerify2FA("email_otp")} disabled={loading || otp.length < 6}
                      className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 transition-colors uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="mfa-email-verify-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("action.login")}
                    </button>
                  </>
                )}
                <div className="mt-6 text-center">
                  <button onClick={handleResendEmailCode} className="text-xs text-cyan-600 hover:text-cyan-700" data-testid="mfa-resend-btn">
                    <RefreshCw className="w-3 h-3 inline mr-1" /> {t("mfa.resend_code", "Resend code")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
